/*==============================================================================
TRESTLE W007
TRESTLE INSIGHT — PDF GENERATOR

Brand        : Diafemme
Module       : W007
File         : TRESTLE_W007_Insight_PDF.js

Version      : 2.0
Status       : PRODUCTION

Responsibilities
----------------
• Generate downloadable TRESTLE Insight PDF
• Use the selected TRESTLE logo from the W007 Shopify section
• Render TRESTLE NOTICE / THREAD / CONTRAST / PROMISE / ONE THING
• Keep Science and Doctor Questions conditional
• Generate client-side only
• No GAS
• No backend
• No personal wellness-data transmission
==============================================================================*/

"use strict";


const TrestleW007PDF = (() => {


/*==============================================================================
CONFIGURATION
==============================================================================*/

const CONFIG = {

    pageWidthPx: 794,

    contentMarginPx: 50,

    filenamePrefix:
        "TRESTLE-Insight",

    brand:
        "TRESTLE by Diafemme"

};


/*==============================================================================
SAFE STRING
==============================================================================*/

function safeString(value) {

    if (
        value === null ||
        value === undefined
    ) {

        return "";

    }

    return String(value).trim();

}


/*==============================================================================
ESCAPE HTML
==============================================================================*/

function escapeHtml(value) {

    return safeString(value)

        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

}


/*==============================================================================
FORMAT DATE
==============================================================================*/

function formatDate(dateString) {

    const value =
        safeString(
            dateString
        );


    if (!value) {

        return "";

    }


    try {

        const parts =
            value.split("-");


        if (parts.length !== 3) {

            return value;

        }


        const date =
            new Date(

                Number(parts[0]),

                Number(parts[1]) - 1,

                Number(parts[2])

            );


        return new Intl.DateTimeFormat(

            "en-IN",

            {

                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric"

            }

        ).format(date);

    }

    catch (error) {

        return value;

    }

}


/*==============================================================================
GET FIRST NAME
==============================================================================*/

function getFirstName(insight) {

    if (
        insight &&
        insight.identity &&
        insight.identity.firstName
    ) {

        return safeString(
            insight.identity.firstName
        );

    }


    if (
        window.TrestleCustomer &&
        window.TrestleCustomer.firstName
    ) {

        return safeString(
            window.TrestleCustomer.firstName
        );

    }


    return "";

}


/*==============================================================================
GET CONFIGURED TRESTLE LOGO
*
*The Liquid section places the selected Shopify image URL
*on #trW007Insight as data-logo-url.
==============================================================================*/

function getConfiguredLogoUrl() {

    return "https://cdn.shopify.com/s/files/1/0588/5250/7777/files/Trestle_Logo-_webp.webp?v=1786607743";

}


/*==============================================================================
NORMALIZE GLANCE ITEM
==============================================================================*/

function normalizeGlanceItem(
    item,
    index
) {

    if (
        item === null ||
        item === undefined
    ) {

        return null;

    }


    if (
        typeof item === "string" ||
        typeof item === "number"
    ) {

        return {

            label:
                `Check-in ${index + 1}`,

            value:
                safeString(item),

            secondary:
                ""

        };

    }


    if (typeof item !== "object") {

        return null;

    }


    return {

        label:
            safeString(

                item.label ||
                item.title ||
                item.name ||
                item.domain ||
                item.type

            ),

        value:
            safeString(

                item.value ||
                item.displayValue ||
                item.summary ||
                item.text ||
                item.status

            ),

        secondary:
            safeString(

                item.secondary ||
                item.detail ||
                item.time ||
                item.note ||
                item.unit

            )

    };

}


/*==============================================================================
SOURCE LIBRARY
==============================================================================*/

function getSourceLibrary() {

    const data =
        window.TRESTLE_W007_INSIGHT_DATA;


    if (
        !data ||
        typeof data !== "object"
    ) {

        return {};

    }


    return (

        data.sourceLibrary ||
        data.references ||
        data.sources ||
        {}

    );

}


/*==============================================================================
RESOLVE SOURCE
==============================================================================*/

function resolveSource(sourceId) {

    const library =
        getSourceLibrary();


    if (Array.isArray(library)) {

        return (

            library.find(

                item =>
                    item &&
                    (
                        item.id === sourceId ||
                        item.sourceId === sourceId
                    )

            ) || null

        );

    }


    return (
        library[sourceId] ||
        null
    );

}


/*==============================================================================
BUILD HEADER
==============================================================================*/

function buildHeader(insight) {

    const logoUrl =
        getConfiguredLogoUrl();


    return `

        <header class="tr-pdf-header">

            <div class="tr-pdf-brand-wrap">

                ${
                    logoUrl

                        ? `
                            <img
                                class="tr-pdf-logo"
                                src="${escapeHtml(logoUrl)}"
                                alt="TRESTLE by Diafemme"
                                crossorigin="anonymous"
                            >
                          `

                        : ""
                }


                <div class="tr-pdf-brand-copy">

                    <span class="tr-pdf-brand">
                        TRESTLE INSIGHT
                    </span>

                    <span class="tr-pdf-brand-sub">
                        Your day, noticed.
                    </span>

                </div>

            </div>


            <span class="tr-pdf-date">
                ${escapeHtml(
                    formatDate(
                        insight.date
                    )
                )}
            </span>

        </header>

    `;

}


/*==============================================================================
BUILD OPENING
==============================================================================*/

function buildOpening(insight) {

    const firstName =
        getFirstName(
            insight
        );


    const greeting =
        firstName

            ? `${firstName}, here is your day reflected back to you.`

            : "Here is your day reflected back to you.";


    return `

        <section class="tr-pdf-opening">

            <span class="tr-pdf-opening-kicker">
                YOUR PERSONAL TRESTLE INSIGHT
            </span>

            <h1>
                ${escapeHtml(greeting)}
            </h1>

            ${
                insight.sparseIntro

                    ? `
                        <p>
                            ${escapeHtml(
                                insight.sparseIntro
                            )}
                        </p>
                      `

                    : ""
            }

        </section>

    `;

}


/*==============================================================================
BUILD DAY AT A GLANCE
==============================================================================*/

function buildGlance(insight) {

    const data =
        insight.dayAtGlance &&
        typeof insight.dayAtGlance === "object"

            ? insight.dayAtGlance

            : {};


    const items =
        Array.isArray(
            data.items
        )

            ? data.items
                .map(
                    normalizeGlanceItem
                )
                .filter(Boolean)

            : [];


    if (!items.length) {

        return "";

    }


    return `

        <section class="tr-pdf-section">

            <div class="tr-pdf-section-head">

                <span class="tr-pdf-section-number">
                    01
                </span>

                <div>

                    <h2>
                        Your Day at a Glance
                    </h2>

                    <p>
                        What you chose to record today.
                    </p>

                </div>

            </div>


            <div class="tr-pdf-glance-grid">

                ${
                    items.map(

                        item => `

                            <div class="tr-pdf-glance-item">

                                <span class="tr-pdf-label">
                                    ${escapeHtml(item.label)}
                                </span>

                                <strong>
                                    ${escapeHtml(item.value)}
                                </strong>

                                ${
                                    item.secondary

                                        ? `
                                            <small>
                                                ${escapeHtml(
                                                    item.secondary
                                                )}
                                            </small>
                                          `

                                        : ""
                                }

                            </div>

                        `

                    ).join("")
                }

            </div>

        </section>

    `;

}


/*==============================================================================
BUILD TRESTLE NOTICE
==============================================================================*/

function buildNotice(insight) {

    const notice =
        insight &&
        insight.notice &&
        typeof insight.notice === "object"

            ? insight.notice

            : null;


    const text =
        notice

            ? safeString(
                notice.text
            )

            : "";


    if (!text) {

        return "";

    }


    return `

        <section
            class="
                tr-pdf-section
                tr-pdf-intelligence
                tr-pdf-notice
            "
        >

            <div class="tr-pdf-section-head">

                <span class="tr-pdf-section-number">
                    02
                </span>

                <div>

                    <span class="tr-pdf-intelligence-kicker">
                        TRESTLE NOTICE
                    </span>

                    <h2>
                        What stood out today
                    </h2>

                    <p>
                        A detail or relationship worth noticing
                        in what you recorded.
                    </p>

                </div>

            </div>


            <div class="tr-pdf-intelligence-box">

                <p>
                    ${escapeHtml(text)}
                </p>

            </div>

        </section>

    `;

}


/*==============================================================================
BUILD TRESTLE THREAD
==============================================================================*/

function buildThread(insight) {

    const thread =
        insight &&
        insight.thread &&
        typeof insight.thread === "object"

            ? insight.thread

            : null;


    const text =
        thread

            ? safeString(
                thread.text
            )

            : "";


    if (!text) {

        return "";

    }


    return `

        <section
            class="
                tr-pdf-section
                tr-pdf-intelligence
                tr-pdf-thread
            "
        >

            <div class="tr-pdf-section-head">

                <span class="tr-pdf-section-number">
                    03
                </span>

                <div>

                    <span class="tr-pdf-intelligence-kicker">
                        TRESTLE THREAD
                    </span>

                    <h2>
                        What keeps appearing
                    </h2>

                    <p>
                        A recurring feature across your available
                        recent recorded days.
                    </p>

                </div>

            </div>


            <div class="tr-pdf-intelligence-box">

                <p>
                    ${escapeHtml(text)}
                </p>


                ${
                    thread.caution

                        ? `
                            <small class="tr-pdf-caution">
                                ${escapeHtml(
                                    thread.caution
                                )}
                            </small>
                          `

                        : ""
                }

            </div>

        </section>

    `;

}


/*==============================================================================
BUILD TRESTLE CONTRAST
==============================================================================*/

function buildContrast(insight) {

    const contrast =
        insight &&
        insight.contrast &&
        typeof insight.contrast === "object"

            ? insight.contrast

            : null;


    const text =
        contrast

            ? safeString(
                contrast.text
            )

            : "";


    if (!text) {

        return "";

    }


    return `

        <section
            class="
                tr-pdf-section
                tr-pdf-intelligence
                tr-pdf-contrast
            "
        >

            <div class="tr-pdf-section-head">

                <span class="tr-pdf-section-number">
                    04
                </span>

                <div>

                    <span class="tr-pdf-intelligence-kicker">
                        TRESTLE CONTRAST
                    </span>

                    <h2>
                        What looked different
                    </h2>

                    <p>
                        A comparison within your own available
                        recorded days.
                    </p>

                </div>

            </div>


            <div class="tr-pdf-intelligence-box">

                <p>
                    ${escapeHtml(text)}
                </p>


                ${
                    contrast.caution

                        ? `
                            <small class="tr-pdf-caution">
                                ${escapeHtml(
                                    contrast.caution
                                )}
                            </small>
                          `

                        : ""
                }

            </div>

        </section>

    `;

}


/*==============================================================================
BUILD YOUR PROMISE
==============================================================================*/

function buildPromise(insight) {

    if (
        !insight.promiseInsight ||
        !safeString(
            insight.promiseInsight.text
        )
    ) {

        return "";

    }


    const promise =
        insight.profile

            ? safeString(
                insight.profile.promise
            )

            : "";


    return `

        <section
            class="
                tr-pdf-section
                tr-pdf-promise-section
            "
        >

            <div class="tr-pdf-section-head">

                <span class="tr-pdf-section-number">
                    05
                </span>

                <div>

                    <span class="tr-pdf-intelligence-kicker">
                        YOUR PROMISE
                    </span>

                    <h2>
                        ${
                            promise

                                ? escapeHtml(
                                    `Your Promise: ${promise}`
                                )

                                : "Your chosen Promise"
                        }
                    </h2>

                    <p>
                        Your direction, reflected in today's evidence.
                    </p>

                </div>

            </div>


            <div class="tr-pdf-promise-box">

                <p>
                    ${escapeHtml(
                        insight.promiseInsight.text
                    )}
                </p>

            </div>

        </section>

    `;

}


/*==============================================================================
BUILD ONE THING
==============================================================================*/

function buildFocus(insight) {

    const text =
        insight.tomorrowFocus

            ? safeString(
                insight.tomorrowFocus.text
            )

            : "";


    if (!text) {

        return "";

    }


    return `

        <section class="tr-pdf-section">

            <div class="tr-pdf-section-head">

                <span class="tr-pdf-section-number">
                    06
                </span>

                <div>

                    <span class="tr-pdf-intelligence-kicker">
                        ONE THING
                    </span>

                    <h2>
                        One thing worth carrying forward
                    </h2>

                    <p>
                        Not a checklist. Just one useful focus for tomorrow.
                    </p>

                </div>

            </div>


            <div class="tr-pdf-focus">

                <p>
                    ${escapeHtml(text)}
                </p>

            </div>

        </section>

    `;

}


/*==============================================================================
BUILD SCIENCE
==============================================================================*/

function buildScience(insight) {

    const science =
        Array.isArray(
            insight.scienceAndYourDay
        )

            ? insight.scienceAndYourDay

            : [];


    if (!science.length) {

        return "";

    }


    const content =
        science

            .map(

                item => {

                    if (!item) {

                        return "";

                    }


                    const text =
                        typeof item === "object"

                            ? safeString(
                                item.text ||
                                item.summary ||
                                item.body
                            )

                            : safeString(item);


                    if (!text) {

                        return "";

                    }


                    const authority =
                        typeof item === "object"

                            ? safeString(

                                item.authority ||
                                item.sourceLabel ||
                                item.source ||
                                item.title

                            )

                            : "";


                    return `

                        <div class="tr-pdf-science">

                            ${
                                authority

                                    ? `
                                        <strong>
                                            ${escapeHtml(authority)}
                                        </strong>
                                      `

                                    : ""
                            }

                            <p>
                                ${escapeHtml(text)}
                            </p>

                        </div>

                    `;

                }

            )

            .filter(Boolean)
            .join("");


    if (!content) {

        return "";

    }


    return `

        <section
            class="
                tr-pdf-section
                tr-pdf-supporting
            "
        >

            <div class="tr-pdf-section-head">

                <span class="tr-pdf-section-number">
                    +
                </span>

                <div>

                    <h2>
                        Science &amp; Your Day
                    </h2>

                    <p>
                        Useful context for what you recorded today.
                    </p>

                </div>

            </div>


            ${content}

        </section>

    `;

}


/*==============================================================================
BUILD DOCTOR QUESTIONS
==============================================================================*/

function buildDoctorQuestions(insight) {

    const questions =
        Array.isArray(
            insight.doctorQuestions
        )

            ? insight.doctorQuestions

            : [];


    if (!questions.length) {

        return "";

    }


    const content =
        questions

            .map(

                item => {

                    const text =
                        typeof item === "object"

                            ? safeString(
                                item.question ||
                                item.text
                            )

                            : safeString(item);


                    return text

                        ? `
                            <li>
                                ${escapeHtml(text)}
                            </li>
                          `

                        : "";

                }

            )

            .filter(Boolean)
            .join("");


    if (!content) {

        return "";

    }


    return `

        <section
            class="
                tr-pdf-section
                tr-pdf-supporting
            "
        >

            <div class="tr-pdf-section-head">

                <span class="tr-pdf-section-number">
                    +
                </span>

                <div>

                    <h2>
                        Questions Worth Carrying to Your Doctor
                    </h2>

                    <p>
                        Only when something you recorded may be useful
                        to discuss at a future consultation.
                    </p>

                </div>

            </div>


            <ul class="tr-pdf-questions">
                ${content}
            </ul>

        </section>

    `;

}


/*==============================================================================
BUILD SOURCES
==============================================================================*/

function buildSources(insight) {

    const ids =
        Array.isArray(
            insight.sourceIds
        )

            ? insight.sourceIds

            : [];


    if (!ids.length) {

        return "";

    }


    const sources =
        ids

            .map(

                id => {

                    const source =
                        resolveSource(id);


                    if (!source) {

                        return "";

                    }


                    const authority =
                        safeString(

                            source.authority ||
                            source.organization ||
                            source.name ||
                            source.title

                        );


                    const title =
                        safeString(

                            source.sourceTitle ||
                            source.title ||
                            source.guidance ||
                            source.description

                        );


                    const year =
                        safeString(

                            source.sourceYear ||
                            source.year

                        );


                    const text = [

                        authority,
                        title,
                        year

                    ]
                        .filter(Boolean)
                        .join(" — ");


                    return text

                        ? `
                            <p>
                                ${escapeHtml(text)}
                            </p>
                          `

                        : "";

                }

            )

            .filter(Boolean)
            .join("");


    if (!sources) {

        return "";

    }


    return `

        <section
            class="
                tr-pdf-section
                tr-pdf-sources
            "
        >

            <div class="tr-pdf-section-head">

                <span class="tr-pdf-section-number">
                    +
                </span>

                <div>

                    <h2>
                        Guidance Referenced
                    </h2>

                    <p>
                        General reference context used by TRESTLE.
                    </p>

                </div>

            </div>


            ${sources}

        </section>

    `;

}


/*==============================================================================
BUILD FOOTER
==============================================================================*/

function buildFooter(insight) {

    return `

        <footer class="tr-pdf-footer">

            <div class="tr-pdf-closing">

                <strong>
                    What you recorded is information, not a verdict.
                </strong>

                ${
                    insight.closing

                        ? `
                            <p>
                                ${escapeHtml(
                                    insight.closing
                                )}
                            </p>
                          `

                        : `
                            <p>
                                TRESTLE notices patterns carefully
                                and leaves medical decisions where
                                they belong — with you and your
                                healthcare professional.
                            </p>
                          `
                }

            </div>


            ${
                insight.disclaimer

                    ? `
                        <p class="tr-pdf-disclaimer">
                            ${escapeHtml(
                                insight.disclaimer
                            )}
                        </p>
                      `

                    : ""
            }


            <span class="tr-pdf-footer-brand">
                ${escapeHtml(CONFIG.brand)}
            </span>

        </footer>

    `;

}


/*==============================================================================
BUILD REPORT HTML
==============================================================================*/

/*==============================================================================
BUILD PAGE 1
CORE TRESTLE INTELLIGENCE
==============================================================================*/

function buildPageOneHtml(insight) {

    return `

        <div class="tr-pdf-report tr-pdf-page tr-pdf-page-one">

            <div
                class="tr-pdf-organic-shape
                       tr-pdf-organic-shape-one"
            ></div>

            ${buildHeader(insight)}

            ${buildOpening(insight)}

            ${buildGlance(insight)}

            ${buildNotice(insight)}

            ${buildThread(insight)}

            ${buildContrast(insight)}

            ${buildPromise(insight)}

            ${buildFocus(insight)}

        </div>

    `;

}


/*==============================================================================
BUILD PAGE 2 CONTINUATION HEADER
==============================================================================*/

function buildContinuationHeader(insight) {

    const logoUrl =
        getConfiguredLogoUrl();


    return `

        <header class="tr-pdf-header">

            <div class="tr-pdf-brand-wrap">

                ${
                    logoUrl

                        ? `
                            <img
                                class="tr-pdf-logo"
                                src="${escapeHtml(logoUrl)}"
                                alt="TRESTLE by Diafemme"
                                crossorigin="anonymous"
                            >
                          `

                        : ""
                }


                <div class="tr-pdf-brand-copy">

                    <span class="tr-pdf-brand">
                        CONTEXT &amp; GUIDANCE
                    </span>

                    <span class="tr-pdf-brand-sub">
                        TRESTLE Insight · continued
                    </span>

                </div>

            </div>


            <span class="tr-pdf-date">
                ${escapeHtml(
                    formatDate(
                        insight.date
                    )
                )}
            </span>

        </header>

    `;

}


/*==============================================================================
BUILD PAGE 2
CONTEXT & GUIDANCE
==============================================================================*/

function buildPageTwoHtml(insight) {

    return `

        <div class="tr-pdf-report tr-pdf-page tr-pdf-page-two">

            <div
                class="tr-pdf-organic-shape
                       tr-pdf-organic-shape-two"
            ></div>


            ${buildContinuationHeader(insight)}

            ${buildScience(insight)}

            ${buildDoctorQuestions(insight)}

            ${buildSources(insight)}

            ${buildFooter(insight)}

        </div>

    `;

}


/*==============================================================================
BUILD COMPLETE TWO-PAGE REPORT
==============================================================================*/

function buildReportHtml(insight) {

    return `

        <div class="tr-pdf-document">

            ${buildPageOneHtml(insight)}

            ${buildPageTwoHtml(insight)}

        </div>

    `;

}


/*==============================================================================
PDF STYLES
==============================================================================*/

function getStyles() {

    return `

        *{
            box-sizing:border-box;
        }


        html,
        body{
            margin:0;
            padding:0;
            background:#ffffff;
        }


        body{
            color:#263b39;
            font-family:
                Arial,
                Helvetica,
                sans-serif;
        }


        .tr-pdf-report{
            position:relative;
            width:${CONFIG.pageWidthPx}px;
            padding:
                44px
                ${CONFIG.contentMarginPx}px
                54px;
            overflow:hidden;
            background:#ffffff;
        }


        /*==============================================================
        SUBTLE STATIC BACKGROUND GRAPHICS
        ===============================================================*/

        .tr-pdf-organic-shape{
            position:absolute;
            z-index:0;
            pointer-events:none;
            border:1px solid rgba(47,117,108,.08);
        }


        .tr-pdf-organic-shape-one{
            top:58px;
            right:-78px;
            width:235px;
            height:235px;
            border-radius:
                48% 52% 62% 38%;
            transform:rotate(28deg);
        }


        .tr-pdf-organic-shape-two{
            left:-102px;
            bottom:110px;
            width:250px;
            height:250px;
            border-color:
                rgba(185,151,91,.08);
            border-radius:
                58% 42% 45% 55%;
            transform:rotate(-18deg);
        }


        .tr-pdf-report > *:not(
            .tr-pdf-organic-shape
        ){
            position:relative;
            z-index:1;
        }


        /*==============================================================
        HEADER
        ===============================================================*/

        .tr-pdf-header{
            display:flex;
            justify-content:space-between;
            align-items:flex-start;
            gap:24px;
            padding-bottom:18px;
            border-bottom:1px solid #dbe6e3;
        }


        .tr-pdf-brand-wrap{
            display:flex;
            align-items:center;
            gap:14px;
        }


        .tr-pdf-logo{
            display:block;
            width:auto;
            max-width:150px;
            max-height:64px;
            object-fit:contain;
        }


        .tr-pdf-brand-copy{
            min-width:0;
        }


        .tr-pdf-brand{
            display:block;
            color:#195e58;
            font-size:15px;
            line-height:1.25;
            font-weight:700;
            letter-spacing:.12em;
        }


        .tr-pdf-brand-sub{
            display:block;
            margin-top:4px;
            color:#70817f;
            font-size:9px;
            line-height:1.4;
            letter-spacing:.04em;
        }


        .tr-pdf-date{
            flex:0 0 auto;
            color:#70817f;
            font-size:10px;
            line-height:1.4;
            text-align:right;
        }


        /*==============================================================
        OPENING
        ===============================================================*/

        .tr-pdf-opening{
            padding:
                28px
                0
                18px;
        }


        .tr-pdf-opening-kicker,
        .tr-pdf-intelligence-kicker{
            display:block;
            margin-bottom:6px;
            color:#27776f;
            font-size:9px;
            line-height:1.3;
            font-weight:700;
            letter-spacing:.16em;
        }


        .tr-pdf-opening h1{
            margin:0;
            max-width:580px;
            color:#24433f;
            font-size:24px;
            line-height:1.28;
            font-weight:500;
            letter-spacing:-.02em;
        }


        .tr-pdf-opening p{
            margin:
                8px
                0
                0;
            max-width:620px;
            color:#657673;
            font-size:11.5px;
            line-height:1.6;
        }


        /*==============================================================
        SECTION
        ===============================================================*/

        .tr-pdf-section{
            margin-top:15px;
            padding-top:15px;
            border-top:1px solid #e2ebe9;
        }


        .tr-pdf-section-head{
            display:grid;
            grid-template-columns:
                31px
                minmax(0,1fr);
            gap:11px;
            align-items:start;
            margin-bottom:12px;
        }


        .tr-pdf-section-number{
            display:flex;
            align-items:center;
            justify-content:center;
            width:28px;
            height:28px;
            border-radius:50%;
            background:#edf7f5;
            color:#36776f;
            font-size:8.5px;
            line-height:1;
            font-weight:700;
        }


        .tr-pdf-section-head h2{
            margin:0;
            color:#245f59;
            font-size:14px;
            line-height:1.3;
            font-weight:650;
        }


        .tr-pdf-section-head p{
            margin:
                3px
                0
                0;
            color:#778986;
            font-size:9.5px;
            line-height:1.45;
        }


        /*==============================================================
        DAY AT A GLANCE
        ===============================================================*/

        .tr-pdf-glance-grid{
            display:grid;
            grid-template-columns:
                repeat(4,1fr);
            gap:7px;
        }


        .tr-pdf-glance-item{
            min-height:67px;
            padding:9px;
            border:1px solid #dce8e5;
            border-radius:8px;
            background:#f8fbfa;
        }


        .tr-pdf-label{
            display:block;
            margin-bottom:5px;
            color:#748582;
            font-size:8px;
            line-height:1.3;
            text-transform:uppercase;
            letter-spacing:.06em;
        }


        .tr-pdf-glance-item strong{
            display:block;
            color:#24433f;
            font-size:11px;
            line-height:1.35;
        }


        .tr-pdf-glance-item small{
            display:block;
            margin-top:3px;
            color:#7c8c89;
            font-size:8px;
            line-height:1.35;
        }


        /*==============================================================
        INTELLIGENCE
        ===============================================================*/

        .tr-pdf-intelligence-box{
            position:relative;
            overflow:hidden;
            padding:
                14px
                16px;
            border:1px solid #dfece8;
            border-radius:10px;
            background:
                linear-gradient(
                    135deg,
                    #f5faf8 0%,
                    #fbf9f3 100%
                );
        }


        .tr-pdf-intelligence-box::after{
            content:"";
            position:absolute;
            right:-32px;
            bottom:-40px;
            width:105px;
            height:105px;
            border:1px solid rgba(48,116,107,.09);
            border-radius:
                58% 42% 62% 38%;
            transform:rotate(24deg);
        }


        .tr-pdf-thread
        .tr-pdf-intelligence-box{
            background:
                linear-gradient(
                    135deg,
                    #eef8f5 0%,
                    #f9fbfa 100%
                );
        }


        .tr-pdf-contrast
        .tr-pdf-intelligence-box{
            background:
                linear-gradient(
                    120deg,
                    #faf7ef 0%,
                    #f2f9f7 100%
                );
        }


        .tr-pdf-intelligence-box p{
            position:relative;
            z-index:1;
            margin:0;
            color:#244d48;
            font-size:11.5px;
            line-height:1.62;
        }


        .tr-pdf-caution{
            position:relative;
            z-index:1;
            display:block;
            margin-top:8px;
            padding-top:7px;
            border-top:1px solid #e2ece9;
            color:#718480;
            font-size:8.5px;
            line-height:1.5;
        }


        /*==============================================================
        PROMISE
        ===============================================================*/

        .tr-pdf-promise-box{
            position:relative;
            overflow:hidden;
            padding:
                14px
                16px;
            border:1px solid rgba(185,151,91,.18);
            border-radius:10px;
            background:
                linear-gradient(
                    135deg,
                    #faf7ef 0%,
                    #f4faf8 100%
                );
        }


        .tr-pdf-promise-box::before{
            content:"“";
            position:absolute;
            left:8px;
            top:2px;
            color:rgba(47,117,108,.11);
            font-family:Georgia,serif;
            font-size:36px;
            line-height:1;
        }


        .tr-pdf-promise-box p{
            position:relative;
            z-index:1;
            margin:0;
            color:#315d58;
            font-size:11.5px;
            line-height:1.65;
        }


        /*==============================================================
        ONE THING
        ===============================================================*/

        .tr-pdf-focus{
            padding:
                14px
                16px;
            border-radius:10px;
            background:
                linear-gradient(
                    135deg,
                    #245f59 0%,
                    #2f766e 100%
                );
        }


        .tr-pdf-focus p{
            margin:0;
            color:#ffffff;
            font-size:11.5px;
            line-height:1.62;
            font-weight:500;
        }


        /*==============================================================
        SCIENCE
        ===============================================================*/

        .tr-pdf-science{
            margin-top:8px;
            padding:
                11px
                13px;
            border:1px solid #dfece9;
            border-radius:9px;
            background:#f9fcfb;
        }


        .tr-pdf-science strong{
            display:block;
            margin-bottom:4px;
            color:#27776f;
            font-size:8.5px;
            line-height:1.4;
            letter-spacing:.04em;
        }


        .tr-pdf-science p{
            margin:0;
            color:#425f5b;
            font-size:10.5px;
            line-height:1.55;
        }


        /*==============================================================
        DOCTOR QUESTIONS
        ===============================================================*/

        .tr-pdf-questions{
            margin:0;
            padding-left:18px;
        }


        .tr-pdf-questions li{
            margin:
                0
                0
                7px;
            color:#415e5a;
            font-size:10.5px;
            line-height:1.55;
        }


        /*==============================================================
        SOURCES
        ===============================================================*/

        .tr-pdf-sources p{
            margin:
                3px
                0;
            color:#687a77;
            font-size:8px;
            line-height:1.4;
        }


        /*==============================================================
        FOOTER
        ===============================================================*/

        .tr-pdf-footer{
            margin-top:22px;
            padding-top:16px;
            border-top:1px solid #dbe6e3;
        }


        .tr-pdf-closing{
            padding:
                12px
                14px;
            border-radius:9px;
            background:
                linear-gradient(
                    135deg,
                    #f6faf8,
                    #fbf8f1
                );
        }


        .tr-pdf-closing strong{
            display:block;
            margin-bottom:5px;
            color:#245f59;
            font-family:
                Georgia,
                "Times New Roman",
                serif;
            font-size:12px;
            line-height:1.45;
            font-style:italic;
            font-weight:500;
        }


        .tr-pdf-closing p{
            margin:0;
            color:#687c78;
            font-size:9px;
            line-height:1.5;
        }


        .tr-pdf-disclaimer{
            margin:
                10px
                0
                0;
            color:#7b8987;
            font-size:7.5px;
            line-height:1.45;
        }


        .tr-pdf-footer-brand{
            display:block;
            margin-top:10px;
            color:#27776f;
            font-size:8px;
            line-height:1.4;
            font-weight:700;
            letter-spacing:.05em;
        }

    `;

}


/*==============================================================================
LOAD SCRIPT
==============================================================================*/

function loadScript(src) {

    return new Promise(

        (resolve, reject) => {

            const existing =
                document.querySelector(
                    `script[src="${src}"]`
                );


            if (existing) {

                if (
                    existing.dataset.loaded ===
                    "true"
                ) {

                    resolve();
                    return;

                }


                if (
                    src.includes(
                        "html2canvas"
                    ) &&
                    window.html2canvas
                ) {

                    resolve();
                    return;

                }


                if (
                    src.includes(
                        "jspdf"
                    ) &&
                    window.jspdf &&
                    window.jspdf.jsPDF
                ) {

                    resolve();
                    return;

                }


                existing.addEventListener(
                    "load",
                    resolve,
                    { once:true }
                );


                existing.addEventListener(
                    "error",
                    reject,
                    { once:true }
                );


                return;

            }


            const script =
                document.createElement(
                    "script"
                );


            script.src =
                src;

            script.async =
                true;


            script.addEventListener(

                "load",

                () => {

                    script.dataset.loaded =
                        "true";

                    resolve();

                },

                { once:true }

            );


            script.addEventListener(

                "error",

                reject,

                { once:true }

            );


            document.head.appendChild(
                script
            );

        }

    );

}


/*==============================================================================
ENSURE PDF LIBRARIES
==============================================================================*/

async function ensureLibraries() {

    if (
        !window.html2canvas
    ) {

        await loadScript(
            "https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"
        );

    }


    if (
        !window.jspdf ||
        !window.jspdf.jsPDF
    ) {

        await loadScript(
            "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"
        );

    }


    if (
        !window.html2canvas ||
        !window.jspdf ||
        !window.jspdf.jsPDF
    ) {

        throw new Error(
            "PDF libraries could not be loaded."
        );

    }

}


/*==============================================================================
WAIT FOR REPORT IMAGES
==============================================================================*/

function waitForImages(container) {

    const images =
        Array.from(
            container.querySelectorAll(
                "img"
            )
        );


    if (!images.length) {

        return Promise.resolve();

    }


    return Promise.all(

        images.map(

            image => {

                if (
                    image.complete &&
                    image.naturalWidth > 0
                ) {

                    return Promise.resolve();

                }


                return new Promise(
                    resolve => {

                        image.addEventListener(
                            "load",
                            resolve,
                            { once:true }
                        );


                        image.addEventListener(
                            "error",
                            resolve,
                            { once:true }
                        );


                        window.setTimeout(
                            resolve,
                            2500
                        );

                    }
                );

            }

        )

    );

}


/*==============================================================================
BUILD FILENAME
==============================================================================*/

function buildFilename(insight) {

    const date =
        safeString(
            insight.date
        ) ||
        new Date()
            .toISOString()
            .slice(0,10);


    return (
        `${CONFIG.filenamePrefix}-${date}.pdf`
    );

}


/*==============================================================================
CREATE OFFSCREEN REPORT
==============================================================================*/

function createReportHost(insight) {

    const host =
        document.createElement(
            "div"
        );


    host.setAttribute(
        "aria-hidden",
        "true"
    );


    host.style.position =
        "fixed";

    host.style.left =
        "-12000px";

    host.style.top =
        "0";

    host.style.width =
        `${CONFIG.pageWidthPx}px`;

    host.style.background =
        "#ffffff";


    host.innerHTML = `

        <style>
            ${getStyles()}
        </style>

        ${buildReportHtml(insight)}

    `;


    document.body.appendChild(
        host
    );


    return host;

}


/*==============================================================================
CREATE CANVAS SLICE
*
*Instead of placing one giant screenshot repeatedly at negative positions,
*this crops each PDF page from the rendered report canvas.
*
*This prevents the almost-empty final page seen in the earlier PDF.
==============================================================================*/

function createCanvasSlice(
    sourceCanvas,
    sourceY,
    sourceHeight
) {

    const slice =
        document.createElement(
            "canvas"
        );


    slice.width =
        sourceCanvas.width;

    slice.height =
        Math.max(
            1,
            Math.round(
                sourceHeight
            )
        );


    const context =
        slice.getContext(
            "2d"
        );


    context.fillStyle =
        "#ffffff";


    context.fillRect(
        0,
        0,
        slice.width,
        slice.height
    );


    context.drawImage(

        sourceCanvas,

        0,
        sourceY,
        sourceCanvas.width,
        sourceHeight,

        0,
        0,
        slice.width,
        slice.height

    );


    return slice;

}


/*==============================================================================
GENERATE PDF
==============================================================================*/

async function generate(insight) {

    if (
        !insight ||
        typeof insight !== "object"
    ) {

        throw new Error(
            "TRESTLE W007 PDF: Insight unavailable."
        );

    }


    await ensureLibraries();


    const host =
        createReportHost(
            insight
        );


    try {

        const pages =
            Array.from(
                host.querySelectorAll(
                    ".tr-pdf-page"
                )
            );


        if (!pages.length) {

            throw new Error(
                "PDF report pages could not be created."
            );

        }


        await waitForImages(
            host
        );


        await new Promise(
            resolve => {

                window.requestAnimationFrame(
                    () => {

                        window.requestAnimationFrame(
                            resolve
                        );

                    }
                );

            }
        );


        const {
            jsPDF
        } =
            window.jspdf;


        const pdf =
            new jsPDF({

                orientation:
                    "portrait",

                unit:
                    "mm",

                format:
                    "a4",

                compress:
                    true

            });


        const pageWidth =
            pdf.internal.pageSize
                .getWidth();


        const pageHeight =
            pdf.internal.pageSize
                .getHeight();


        const sideMargin =
            10;


        const topMargin =
            9;


        const bottomMargin =
            11;


        const usableWidth =
            pageWidth -
            (
                sideMargin *
                2
            );


        const usableHeight =
            pageHeight -
            topMargin -
            bottomMargin;


        for (
            let index = 0;
            index < pages.length;
            index++
        ) {

            const page =
                pages[index];


            const canvas =
                await window.html2canvas(

                    page,

                    {

                        scale:2,

                        backgroundColor:
                            "#ffffff",

                        useCORS:true,

                        logging:false,

                        imageTimeout:3000,

                        removeContainer:true

                    }

                );


            if (
                index > 0
            ) {

                pdf.addPage();

            }


            const imageData =
                canvas.toDataURL(

                    "image/jpeg",

                    0.95

                );


            const naturalHeightMm =
                usableWidth *
                (
                    canvas.height /
                    canvas.width
                );


            /*
            If a designed page becomes taller than A4,
            scale that complete page proportionately.

            This avoids cutting a TRESTLE section in half.
            */

            let imageWidth =
                usableWidth;


            let imageHeight =
                naturalHeightMm;


            if (
                imageHeight >
                usableHeight
            ) {

                const scale =
                    usableHeight /
                    imageHeight;


                imageHeight =
                    usableHeight;


                imageWidth =
                    usableWidth *
                    scale;

            }


            const horizontalOffset =
                sideMargin +
                (
                    usableWidth -
                    imageWidth
                ) / 2;


            pdf.addImage(

                imageData,

                "JPEG",

                horizontalOffset,

                topMargin,

                imageWidth,

                imageHeight,

                undefined,

                "FAST"

            );

        }


        pdf.save(
            buildFilename(
                insight
            )
        );


        return true;

    }

    finally {

        host.remove();

    }

}


/*==============================================================================
PUBLIC API
==============================================================================*/

return {

    generate

};

})();


/*==============================================================================
PUBLIC NAMESPACE
==============================================================================*/

window.TrestleW007PDF =
    TrestleW007PDF;


console.info(
    "TRESTLE W007 PDF Generator v2 Ready"
);